import type {
  IndodaxWsAuthRequest,
  IndodaxWsOrderbookMessage,
  IndodaxWsPingRequest,
  IndodaxWsSubscribeRequest,
  IndodaxWsTickerMessage,
  IndodaxWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

/**
 * Indodax WebSocket base URL.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md
 */
export const INDODAX_WS_BASE_URL = 'wss://ws3.indodax.com/ws/';

/**
 * Indodax WebSocket static public token.
 * This token is publicly available and used for anonymous authentication.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md#authentication
 */
export const INDODAX_WS_STATIC_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE5NDY2MTg0MTV9.UR1lBM6Eqh0yWz-PVirw1uPCxe60FdchR8eNVdsskeo';

/**
 * Indodax WebSocket channel descriptors.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md
 */
export const INDODAX_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  orderbook: {
    channel: 'orderbook',
    topic: 'market:order-book-<pair>',
    supportsBatching: false,
    docUrl:
      'https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md#orderbook',
  },
  trades: {
    channel: 'trades',
    topic: 'market:trade-activity-<pair>',
    supportsBatching: false,
    docUrl:
      'https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md#trade-activity',
  },
  ticker: {
    channel: 'ticker',
    topic: 'market:summary-24h',
    supportsBatching: false,
    docUrl:
      'https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md#market-summary',
  },
  tick: {
    channel: 'tick',
    topic: 'chart:tick-<pair>',
    supportsBatching: false,
    docUrl:
      'https://github.com/btcid/indodax-official-api-docs/blob/master/Marketdata-websocket.md#chart-data',
  },
};

/**
 * Indodax WebSocket topology information.
 * IMPORTANT:
 * - Requires token authentication on connect (static public token)
 * - Uses numeric method field: 1 = subscribe, 2 = unsubscribe, 7 = ping
 * - Plain JSON messages (no compression)
 * - Full orderbook snapshots (not incremental)
 */
export const INDODAX_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: INDODAX_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 25000, // Ping every 25 seconds to maintain connection
  note: 'Requires auth token on connect. Plain JSON, no compression. Methods: 1=subscribe, 2=unsubscribe, 7=ping. Full orderbook snapshots.',
};

// ========================================
// Message Counter (for unique IDs)
// ========================================

let messageIdCounter = 1;

/**
 * Generates a unique message ID for Indodax WebSocket requests.
 */
export function getIndodaxMessageId(): number {
  return messageIdCounter++;
}

/**
 * Resets the message ID counter (useful for testing).
 */
export function resetIndodaxMessageId(): void {
  messageIdCounter = 1;
}

// ========================================
// Message Builders
// ========================================

/**
 * Creates an Indodax WebSocket authentication message.
 * Must be sent immediately after connection.
 *
 * @example
 * ws.send(JSON.stringify(createIndodaxAuthMessage()));
 */
export function createIndodaxAuthMessage(): IndodaxWsAuthRequest {
  return {
    params: {
      token: INDODAX_WS_STATIC_TOKEN,
    },
    id: getIndodaxMessageId(),
  };
}

/**
 * Creates an Indodax WebSocket subscribe message.
 *
 * @param channel - Channel name (e.g., "market:order-book-btcidr")
 *
 * @example
 * ws.send(JSON.stringify(createIndodaxSubscribeMessage('market:order-book-btcidr')));
 */
export function createIndodaxSubscribeMessage(channel: string): IndodaxWsSubscribeRequest {
  return {
    method: 1, // 1 = subscribe
    params: {
      channel,
    },
    id: getIndodaxMessageId(),
  };
}

/**
 * Creates an Indodax WebSocket unsubscribe message.
 *
 * @param channel - Channel name (e.g., "market:order-book-btcidr")
 */
export function createIndodaxUnsubscribeMessage(channel: string): IndodaxWsSubscribeRequest {
  return {
    method: 2, // 2 = unsubscribe
    params: {
      channel,
    },
    id: getIndodaxMessageId(),
  };
}

/**
 * Creates an Indodax WebSocket ping message.
 * Send periodically to maintain connection.
 *
 * @example
 * ws.send(JSON.stringify(createIndodaxPingMessage()));
 */
export function createIndodaxPingMessage(): IndodaxWsPingRequest {
  return {
    method: 7, // 7 = ping
    params: {},
    id: getIndodaxMessageId(),
  };
}

// ========================================
// Channel Builders
// ========================================

/**
 * Builds the orderbook channel name for a pair.
 * @param pair - Trading pair (e.g., "btcidr" - lowercase, no separator)
 *
 * @example
 * buildIndodaxOrderbookChannel('btcidr') // 'market:order-book-btcidr'
 */
export function buildIndodaxOrderbookChannel(pair: string): string {
  return `market:order-book-${pair.toLowerCase()}`;
}

/**
 * Builds the trades channel name for a pair.
 * @param pair - Trading pair (e.g., "btcidr" - lowercase, no separator)
 *
 * @example
 * buildIndodaxTradesChannel('btcidr') // 'market:trade-activity-btcidr'
 */
export function buildIndodaxTradesChannel(pair: string): string {
  return `market:trade-activity-${pair.toLowerCase()}`;
}

/**
 * Builds the 24h ticker summary channel name.
 * Note: This is a global channel, not pair-specific.
 *
 * @example
 * buildIndodaxTickerChannel() // 'market:summary-24h'
 */
export function buildIndodaxTickerChannel(): string {
  return 'market:summary-24h';
}

/**
 * Builds the tick (real-time price) channel name for a pair.
 * @param pair - Trading pair (e.g., "btcidr" - lowercase, no separator)
 *
 * @example
 * buildIndodaxTickChannel('btcidr') // 'chart:tick-btcidr'
 */
export function buildIndodaxTickChannel(pair: string): string {
  return `chart:tick-${pair.toLowerCase()}`;
}

// ========================================
// Type Guards
// ========================================

/**
 * Type guard to check if a message is an Indodax orderbook update.
 */
export function isIndodaxOrderbookMessage(msg: unknown): msg is IndodaxWsOrderbookMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as IndodaxWsOrderbookMessage;
  return (
    typeof m.result === 'object' &&
    m.result !== null &&
    typeof m.result.channel === 'string' &&
    m.result.channel.startsWith('market:order-book-') &&
    typeof m.result.data === 'object' &&
    m.result.data !== null &&
    typeof m.result.data.data === 'object'
  );
}

/**
 * Type guard to check if a message is an Indodax trade update.
 */
export function isIndodaxTradeMessage(msg: unknown): msg is IndodaxWsTradeMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as IndodaxWsTradeMessage;
  return (
    typeof m.result === 'object' &&
    m.result !== null &&
    typeof m.result.channel === 'string' &&
    m.result.channel.startsWith('market:trade-activity-') &&
    typeof m.result.data === 'object' &&
    m.result.data !== null &&
    Array.isArray(m.result.data.data)
  );
}

/**
 * Type guard to check if a message is an Indodax ticker (24h summary) update.
 */
export function isIndodaxTickerMessage(msg: unknown): msg is IndodaxWsTickerMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as IndodaxWsTickerMessage;
  return (
    typeof m.result === 'object' &&
    m.result !== null &&
    typeof m.result.channel === 'string' &&
    m.result.channel === 'market:summary-24h' &&
    typeof m.result.data === 'object' &&
    m.result.data !== null &&
    Array.isArray(m.result.data.data)
  );
}

/**
 * Type guard to check if a message is an Indodax pong response.
 */
export function isIndodaxPongResponse(msg: unknown): boolean {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as { id?: number; result?: unknown };
  // Official docs show pong response can be { "id": <n> } (no result field).
  return typeof m.id === 'number';
}

/**
 * Type guard to check if a message is an Indodax auth response.
 */
export function isIndodaxAuthResponse(msg: unknown): boolean {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as { id?: number; result?: { client?: string } };
  return (
    typeof m.id === 'number' &&
    typeof m.result === 'object' &&
    m.result !== null &&
    typeof m.result.client === 'string'
  );
}
